import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Star, ChevronLeft, ChevronRight, Quote, MapPin, Calendar } from 'lucide-react';
import happyCustomersImage from '@/assets/happy-customers-testimonial.jpg';

interface Testimonial {
  id: string;
  name: string;
  location: string;
  rating: number;
  text: string;
  project_type: string;
  date: string;
  image_url?: string;
}

const TestimonialsSection = () => {
  const { language } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);

  // In a real app, this would come from Supabase
  const testimonials: Testimonial[] = [
    {
      id: '1',
      name: 'Sarah Johnson',
      location: 'The Woodlands, TX',
      rating: 5,
      text: language === 'en'
        ? 'The Grass Depot transformed our backyard into a stunning oasis. Their St. Augustine sod installation was flawless, and the team was professional throughout the entire process. Our lawn has never looked better!'
        : 'The Grass Depot transformó nuestro patio trasero en un oasis impresionante. Su instalación de césped St. Augustine fue perfecta, y el equipo fue profesional durante todo el proceso. ¡Nuestro césped nunca se ha visto mejor!',
      project_type: language === 'en' ? 'Residential Sod Installation' : 'Instalación Residencial de Césped',
      date: '2024-03-15'
    },
    {
      id: '2',
      name: 'Michael Rodriguez',
      location: 'Cypress, TX',
      rating: 5,
      text: language === 'en'
        ? 'Outstanding bulk delivery service! They delivered 20 cubic yards of premium topsoil exactly where we needed it. The quality was exceptional and the pricing was very competitive. Highly recommend!'
        : '¡Servicio de entrega a granel sobresaliente! Entregaron 20 yardas cúbicas de tierra vegetal premium exactamente donde la necesitábamos. La calidad fue excepcional y los precios muy competitivos. ¡Altamente recomendado!',
      project_type: language === 'en' ? 'Bulk Material Delivery' : 'Entrega de Material a Granel',
      date: '2024-03-10'
    },
    {
      id: '3',
      name: 'Jennifer Chen',
      location: 'Magnolia, TX',
      rating: 5,
      text: language === 'en'
        ? 'From quote to completion, The Grass Depot exceeded our expectations. They helped us select the perfect mulch for our landscape design and delivered it on time. The difference in our garden is remarkable!'
        : 'Desde la cotización hasta la finalización, The Grass Depot superó nuestras expectativas. Nos ayudaron a seleccionar el mantillo perfecto para nuestro diseño de paisaje y lo entregaron a tiempo. ¡La diferencia en nuestro jardín es notable!',
      project_type: language === 'en' ? 'Landscape Design & Mulch' : 'Diseño de Paisaje y Mantillo',
      date: '2024-03-05'
    },
    {
      id: '4',
      name: 'David Thompson',
      location: 'Conroe, TX',
      rating: 5,
      text: language === 'en'
        ? 'Professional, reliable, and knowledgeable. The team at The Grass Depot helped us choose the right rock materials for our drainage project. Everything was delivered on schedule and within budget.'
        : 'Profesionales, confiables y conocedores. El equipo de The Grass Depot nos ayudó a elegir los materiales de roca correctos para nuestro proyecto de drenaje. Todo fue entregado a tiempo y dentro del presupuesto.',
      project_type: language === 'en' ? 'Drainage & Rock Installation' : 'Drenaje e Instalación de Roca',
      date: '2024-02-28'
    },
    {
      id: '5',
      name: 'Lisa Martinez',
      location: 'Spring, TX',
      rating: 5,
      text: language === 'en'
        ? 'The Grass Depot made our dream landscape a reality. Their expertise in soil preparation and sod installation showed in the final result. Our new Bermuda lawn is the envy of the neighborhood!'
        : 'The Grass Depot hizo realidad nuestro paisaje soñado. Su experiencia en preparación del suelo e instalación de césped se mostró en el resultado final. ¡Nuestro nuevo césped Bermuda es la envidia del vecindario!',
      project_type: language === 'en' ? 'Complete Lawn Renovation' : 'Renovación Completa del Césped',
      date: '2024-02-20'
    }
  ];

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  // Auto-advance testimonials
  useEffect(() => {
    const interval = setInterval(nextTestimonial, 5000);
    return () => clearInterval(interval);
  }, []);

  const currentTestimonial = testimonials[currentIndex];

  return (
    <section className="py-20 bg-muted/30">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            {language === 'en' ? 'What Our Customers Say' : 'Lo Que Dicen Nuestros Clientes'}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {language === 'en'
              ? 'Read success stories from homeowners and businesses across Montgomery County'
              : 'Lee historias de éxito de propietarios y empresas en todo el Condado de Montgomery'
            }
          </p>
        </div>

        {/* Main Testimonial Display */}
        <div className="max-w-4xl mx-auto mb-12">
          <Card className="relative overflow-hidden shadow-glow border-2 border-primary/20">
            <CardContent className="p-8 md:p-12">
              <div className="md:flex md:items-center md:space-x-8">
                {/* Customer Image */}
                <div className="md:w-1/3 mb-6 md:mb-0">
                  <div className="relative w-64 h-48 mx-auto md:mx-0 rounded-lg overflow-hidden">
                    <img 
                      src={happyCustomersImage} 
                      alt="Happy satisfied customers"
                      className="w-full h-full object-cover"
                      width="256"
                      height="192"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                  </div>
                </div>
                
                {/* Testimonial Content */}
                <div className="md:w-2/3 text-center md:text-left">
                  {/* Quote Icon */}
                  <Quote className="h-12 w-12 text-primary/30 mx-auto md:mx-0 mb-6" />
                
                  {/* Rating Stars */}
                  <div className="flex justify-center md:justify-start mb-6">
                    {[...Array(currentTestimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-6 w-6 text-yellow-500 fill-current" />
                    ))}
                  </div>

                  {/* Testimonial Text */}
                  <blockquote className="text-xl md:text-2xl text-foreground font-medium leading-relaxed mb-8">
                    "{currentTestimonial.text}"
                  </blockquote>

                  {/* Customer Info */}
                  <div className="space-y-2">
                    <h3 className="text-lg font-bold text-foreground">
                      {currentTestimonial.name}
                    </h3>
                    <div className="flex items-center justify-center md:justify-start text-muted-foreground">
                      <MapPin className="h-4 w-4 mr-1" />
                      {currentTestimonial.location}
                    </div>
                    <div className="flex items-center justify-center md:justify-start text-sm text-primary">
                      <Calendar className="h-4 w-4 mr-1" />
                      {currentTestimonial.project_type}
                    </div>
                  </div>
                </div>
              </div>

              {/* Navigation Arrows */}
              <div className="absolute top-1/2 left-4 transform -translate-y-1/2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={prevTestimonial}
                  className="rounded-full bg-background/80 hover:bg-background"
                  aria-label={language === 'en' ? 'Previous testimonial' : 'Testimonio anterior'}
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
              </div>
              <div className="absolute top-1/2 right-4 transform -translate-y-1/2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={nextTestimonial}
                  className="rounded-full bg-background/80 hover:bg-background"
                  aria-label={language === 'en' ? 'Next testimonial' : 'Siguiente testimonio'}
                >
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Testimonial Indicators */}
        <div className="flex justify-center space-x-3 mb-12">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-6 h-6 rounded-full transition-all duration-300 ${
                index === currentIndex 
                  ? 'bg-primary' 
                  : 'bg-primary/30 hover:bg-primary/50'
              }`}
              aria-label={`${language === 'en' ? 'Go to testimonial' : 'Ir al testimonio'} ${index + 1}`}
            />
          ))}
        </div>

        {/* Additional Testimonial Cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {testimonials.slice(0, 3).map((testimonial, index) => (
            <Card key={testimonial.id} className="hover:shadow-glow transition-all duration-300">
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h4 className="font-bold text-foreground">{testimonial.name}</h4>
                    <p className="text-sm text-muted-foreground">{testimonial.location}</p>
                  </div>
                  <div className="flex">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 text-yellow-500 fill-current" />
                    ))}
                  </div>
                </div>
                <p className="text-muted-foreground text-sm line-clamp-4 mb-4">
                  "{testimonial.text}"
                </p>
                <div className="text-xs text-primary font-medium">
                  {testimonial.project_type}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <p className="text-lg text-muted-foreground mb-6">
            {language === 'en'
              ? 'Ready to join our satisfied customers?'
              : '¿Listo para unirte a nuestros clientes satisfechos?'
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="hero" size="lg">
              {language === 'en' ? 'Get Your Free Quote' : 'Obtén tu Cotización Gratis'}
            </Button>
            <a href="tel:9363200763">
              <Button variant="outline" size="lg">
                {language === 'en' ? 'Call Us Today' : 'Llámanos Hoy'}
              </Button>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;